import { useRef } from 'react'

/**
 * Horizontal swipe, for stepping between holes on a phone.
 *
 * # Only a deliberate sideways swipe counts
 *
 * The page still scrolls up and down under a thumb, and a tap still opens a
 * shot's card, so a gesture counts only when it travels at least `MIN_PX`
 * sideways and clearly more sideways than down. Anything else is left to the
 * browser. Touch only: a mouse drag on a desktop is not a swipe, and the
 * previous/next links and the arrow keys are there instead.
 */
const MIN_PX = 60
const SIDEWAYS = 1.5

export function useSwipe(onLeft: (() => void) | null, onRight: (() => void) | null) {
  const start = useRef<{ x: number; y: number } | null>(null)
  return {
    onTouchStart: (e: React.TouchEvent) => {
      // A swipe that starts inside something that scrolls sideways itself --
      // the shot table on a phone -- is that thing's, not a change of hole.
      const inScroller = (e.target as Element | null)?.closest?.('.scroll-x')
      if (e.touches.length !== 1 || inScroller) {
        start.current = null
        return
      }
      start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    },
    onTouchEnd: (e: React.TouchEvent) => {
      const s = start.current
      start.current = null
      if (!s || e.changedTouches.length === 0) return
      const dx = e.changedTouches[0].clientX - s.x
      const dy = e.changedTouches[0].clientY - s.y
      if (Math.abs(dx) < MIN_PX || Math.abs(dx) < Math.abs(dy) * SIDEWAYS) return
      // Swiping LEFT moves the page on, as turning a page does.
      if (dx < 0) onLeft?.()
      else onRight?.()
    },
  }
}
